/**
 * appSession.ts
 *
 * Centralized app session read/write helpers.
 *
 * SECURITY POLICY
 * ===============
 * 1. The raw Google ID token (credential) is NEVER stored โ€” not in
 *    localStorage, not in sessionStorage, not anywhere.  It stays in
 *    memory only for the duration of the exchange call.
 *
 * 2. The app session token (HorPlus/Wizard HMAC-SHA256 JWT issued by the
 *    Worker backend) is stored in sessionStorage so it:
 *      - survives page reload within the same browser tab
 *      - is cleared automatically when the tab/browser closes
 *      - is NOT shared between tabs (safe for multi-account dev testing)
 *
 * 3. Only non-sensitive owner display metadata (email, displayName, avatarUrl)
 *    is persisted in localStorage under the key 'worker_owner' for
 *    isLoggedIn detection across tabs.  It contains NO token.
 *
 * 4. Legacy keys (mock_google_session, mock_google_user) from the old mock
 *    flow are cleared on every real login so they cannot persist.
 *
 * 5. Console logging of token values is forbidden.  Log only boolean flags
 *    and safe shapes:
 *      GOOGLE_CREDENTIAL_RECEIVED = true
 *      APP_SESSION_CREATED        = true
 *      TENANT_ID_PRESENT          = true/false
 *
 * 6. The local-dev bridge (localDevSessionBridge.ts) writes its local-dev-*
 *    tokens to the same SESSION_TOKEN_KEY so this module reads them
 *    transparently.  Local-dev tokens are prefixed 'local-dev-' and are
 *    harmless on their own.
 */

// โ”€โ”€โ”€ Storage keys โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€

/** sessionStorage key for the app session token (never localStorage) */
export const SESSION_TOKEN_KEY = 'wiz_session_token';

/** sessionStorage key for the tenant id */
export const SESSION_TENANT_ID_KEY = 'wiz_tenant_id';

/** sessionStorage key for has-tenant boolean */
export const SESSION_HAS_TENANT_KEY = 'wiz_has_tenant';

/** localStorage key for owner display metadata (no token, no credential) */
export const LS_OWNER_KEY = 'worker_owner';

/** localStorage key for tenant onboarding status */
export const LS_TENANT_STATUS_KEY = 'worker_tenant_status';

/** localStorage key: sentinel for local-dev bridge */
export const LS_LOCAL_DEV_BRIDGE_KEY = 'local_dev_bridge_active';

// โ”€โ”€โ”€ Write โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€

export interface SessionWritePayload {
  /** The HorPlus/Wizard app session token (NOT Google token) */
  sessionToken: string;
  /** The tenant id, if the owner already has one */
  tenantId: string | null;
  /** Whether the owner has an existing tenant */
  hasTenant: boolean;
  /** Safe owner display fields (no raw credential, no private token) */
  owner: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl?: string;
  };
}

/**
 * Persists a new app session after successful backend exchange.
 * - App session token โ’ sessionStorage (tab-scoped, auto-cleared on close)
 * - Owner display metadata โ’ localStorage (no token)
 * - Clears legacy mock_ keys so they cannot persist alongside a real session
 */
export function writeAppSession(payload: SessionWritePayload): void {
  // App session token in sessionStorage only
  sessionStorage.setItem(SESSION_TOKEN_KEY, payload.sessionToken);
  sessionStorage.setItem(SESSION_HAS_TENANT_KEY, String(payload.hasTenant));
  if (payload.tenantId) {
    sessionStorage.setItem(SESSION_TENANT_ID_KEY, payload.tenantId);
  } else {
    sessionStorage.removeItem(SESSION_TENANT_ID_KEY);
  }

  // Owner display metadata (no credential, no token) in localStorage
  // for cross-tab isLoggedIn detection only
  localStorage.setItem(LS_OWNER_KEY, JSON.stringify({
    id: payload.owner.id,
    email: payload.owner.email,
    displayName: payload.owner.displayName,
    avatarUrl: payload.owner.avatarUrl || '',
  }));

  // Clear legacy mock keys so they cannot shadow the real session
  localStorage.removeItem('mock_google_session');
  localStorage.removeItem('mock_google_user');
  // Clear old token-in-localStorage keys (migrated to sessionStorage)
  localStorage.removeItem('worker_session_token');
  localStorage.removeItem('worker_tenant_id');
  localStorage.removeItem('worker_has_tenant');

  // Safe log โ€” no token values
  console.info(
    '[APP SESSION] Session written to sessionStorage.',
    'APP_SESSION_CREATED=true',
    'TENANT_ID_PRESENT=' + String(!!payload.tenantId)
  );
}

/**
 * Persists tenantId after createTenant succeeds (Step 1 next).
 */
export function writeSessionTenantId(tenantId: string): void {
  sessionStorage.setItem(SESSION_TENANT_ID_KEY, tenantId);
  sessionStorage.setItem(SESSION_HAS_TENANT_KEY, 'true');
}

/**
 * Marks tenant status as active after onboarding completion.
 */
export function writeSessionTenantActive(): void {
  localStorage.setItem(LS_TENANT_STATUS_KEY, 'active');
}

// โ”€โ”€โ”€ Read โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€

/**
 * Reads the app session token from sessionStorage.
 * Falls back to legacy localStorage key 'worker_session_token' for
 * backward compatibility with local-dev bridge which still uses that key.
 */
export function readSessionToken(): string | null {
  return (
    sessionStorage.getItem(SESSION_TOKEN_KEY) ||
    localStorage.getItem('worker_session_token') ||   // local-dev bridge fallback
    null
  );
}

/**
 * Reads the tenant id from sessionStorage.
 * Falls back to legacy localStorage key 'worker_tenant_id'.
 */
export function readSessionTenantId(): string | null {
  return (
    sessionStorage.getItem(SESSION_TENANT_ID_KEY) ||
    localStorage.getItem('worker_tenant_id') ||       // local-dev bridge fallback
    null
  );
}

/**
 * Returns the safe owner display metadata from localStorage.
 * Returns null if no session exists.
 */
export function readOwnerFromStorage(): Record<string, string> | null {
  // Check sessionStorage token presence as the authority
  const hasSession = !!readSessionToken();
  if (!hasSession) return null;

  // Try new key first, then legacy key
  const raw =
    localStorage.getItem(LS_OWNER_KEY) ||
    localStorage.getItem('worker_owner');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Returns true if an app session token exists in storage.
 */
export function hasActiveSession(): boolean {
  return !!readSessionToken();
}

// โ”€โ”€โ”€ Clear โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€

/**
 * Clears all app session state from sessionStorage and localStorage.
 * Called on logout.
 */
export function clearAppSession(): void {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_TENANT_ID_KEY);
  sessionStorage.removeItem(SESSION_HAS_TENANT_KEY);

  localStorage.removeItem(LS_OWNER_KEY);
  localStorage.removeItem(LS_TENANT_STATUS_KEY);
  localStorage.removeItem(LS_LOCAL_DEV_BRIDGE_KEY);

  // Legacy keys
  localStorage.removeItem('mock_google_session');
  localStorage.removeItem('mock_google_user');
  localStorage.removeItem('worker_session_token');
  localStorage.removeItem('worker_tenant_id');
  localStorage.removeItem('worker_has_tenant');
  localStorage.removeItem('worker_owner');
  localStorage.removeItem('app_tenant_id');

  console.info('[APP SESSION] Session cleared. USER_LOGGED_OUT=true');
}

function getOwnerKey(identifier: string): string {
  return `horplus.setupDraft.${btoa(identifier)}`;
}

export function writeSafeSetupDraft(identifier: string, draft: any): void {
  if (!identifier) return;
  const key = getOwnerKey(identifier);
  localStorage.setItem(key, JSON.stringify({ ...draft, _ownerKey: identifier }));
}

/**
 * Reads a safe draft snapshot from localStorage using the owner's identity.
 */
export function readSafeSetupDraft(identifier: string): Partial<any> | null {
  if (!identifier) return null;
  const draftKey = getOwnerKey(identifier);
  const draftStr = localStorage.getItem(draftKey);
  if (draftStr) {
    try {
      const draft = JSON.parse(draftStr);
      // Ensure owner match is enforced
      if (draft && typeof draft === 'object' && draft.dormName && draft._ownerKey === identifier) {
        return draft;
      }
    } catch (e) {
      console.warn("Failed to load local draft");
    }
  }
  return null;
}

export function readLastSafeSetupDraft(currentOwnerIdentifier?: string): Partial<any> | null {
  // If an owner identifier is provided, only return their draft!
  if (currentOwnerIdentifier) {
    return readSafeSetupDraft(currentOwnerIdentifier);
  }

  // Only for completely unauthenticated anonymous fallback (e.g. preview)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('horplus.setupDraft.')) {
      const draftStr = localStorage.getItem(key);
      if (draftStr) {
        try {
          const draft = JSON.parse(draftStr);
          if (draft && typeof draft === 'object' && draft.dormName) {
            return draft;
          }
        } catch (e) {}
      }
    }
  }
  return null;
}

export function clearOwnerDraft(identifier: string): void {
  if (!identifier) return;
  localStorage.removeItem(getOwnerKey(identifier));
  // Clean legacy keys as well to ensure total isolation transition
  localStorage.removeItem(`wizard_draft_${btoa(identifier)}`);
  localStorage.removeItem(`wizard_draft_${identifier}`);
}
